import React, { useState } from 'react';
import { FileText, Download, ChevronDown, ChevronUp, CheckCircle, Circle, ArrowLeft, ClipboardList, School, Heart, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SECTIONS = [
  {
    id: 'base',
    titre: 'Demande de base',
    description: 'Les documents obligatoires pour toute demande MDPH',
    icon: ClipboardList,
    couleur: { bg: '#FEE2D4', text: '#9A3412', light: '#FFF4F0' },
    formulaires: [
      {
        id: 1,
        cerfa: 'Cerfa 15692*01',
        titre: 'Formulaire de demande / renouvellement',
        qui: 'Rempli par le parent',
        description: "C'est le formulaire principal de toute demande MDPH. Il décrit la situation de votre enfant, ses besoins, et les aides que vous sollicitez (AEEH, orientation, matériel pédagogique, transport…). À remplir à chaque nouvelle demande et à chaque renouvellement.",
        url: 'https://www.service-public.gouv.fr/particuliers/vosdroits/R19993',
      },
      {
        id: 2,
        cerfa: 'Cerfa 15695*01',
        titre: 'Certificat médical obligatoire',
        qui: 'Rempli par le médecin',
        description: "Ce certificat doit être complété par un médecin (généraliste ou spécialiste). Il décrit le diagnostic, les déficiences et leur impact sur la vie quotidienne. Il doit dater de moins de 12 mois, être signé et tamponné. Sans lui, le dossier est incomplet.",
        url: 'https://www.service-public.gouv.fr/particuliers/vosdroits/R19996',
      },
    ],
  },
  {
    id: 'medicaux',
    titre: 'Volets médicaux annexes',
    description: 'À joindre uniquement selon la situation de votre enfant',
    icon: Heart,
    couleur: { bg: '#FCE7F3', text: '#9D174D', light: '#FFF0F8' },
    formulaires: [
      {
        id: 3,
        cerfa: 'Cerfa 15695 — Volets annexes',
        titre: 'Bilans auditif et ophtalmologique',
        qui: "Rempli par l'ORL (volet 1) ou l'ophtalmologue (volet 2)",
        description: "Ces volets sont des annexes du certificat médical 15695. Le volet 1 est rempli par l'ORL si votre enfant présente une déficience auditive. Le volet 2 est rempli par l'ophtalmologue si votre enfant présente une déficience visuelle. C'est le même document — seules les cases à remplir diffèrent selon le spécialiste concerné.",
        url: 'https://www.monparcourshandicap.gouv.fr/demarches/certificat-medical-formulaire-cerfa-ndeg-1569501',
      },
      {
        id: 7,
        cerfa: 'Questionnaire complémentaire PCMT',
        titre: 'Volet médical + retentissements quotidiens',
        qui: 'Volet 1 : médecin spécialiste — Volet 2 : parent ou entourage',
        description: "Document complémentaire spécifique aux troubles neuro-développementaux (TSA, troubles cognitifs, troubles du langage, TDAH…). Le volet 1 est rempli par le médecin spécialiste. Le volet 2 est rempli par le parent et décrit l'impact du handicap sur la vie quotidienne de l'enfant. Non obligatoire mais vivement recommandé pour les dossiers TSA.",
        url: 'https://www.autisme-france.fr/f/b9ce1d961f9800bcee12a34107d64a62a06161bd/QuestionnaireComplementaire-au-DossierMDPH.PCH.PCMT.pdf',
      },
    ],
  },
  {
    id: 'scolaire',
    titre: 'Documents scolaires',
    description: "Transmis par l'école directement à la MDPH",
    icon: School,
    couleur: { bg: '#EDE9FE', text: '#5B21B6', light: '#F5F3FF' },
    formulaires: [
      {
        id: 5,
        cerfa: 'GEVA-Sco',
        titre: "Guide d'évaluation scolaire (1ère demande et réexamen)",
        qui: "Rempli par l'équipe éducative de l'école",
        description: "Le GEVA-Sco est rempli par l'école lors de toute demande ou renouvellement de scolarisation adaptée (AESH, matériel pédagogique, orientation…). La version 'première demande' s'utilise si votre enfant n'a pas encore de Projet Personnalisé de Scolarisation (PPS). La version 'réexamen' s'utilise si un PPS existe déjà. La page officielle propose les deux formulaires à télécharger.",
        url: 'https://www.education.gouv.fr/bo/15/Hebdo8/MENE1502719A.htm',
      },
    ],
  },
  {
    id: 'aides',
    titre: 'Aides et compensation (PCH)',
    description: "Pour les demandes d'aide humaine, matérielle ou logement",
    icon: Home,
    couleur: { bg: '#DBEAFE', text: '#1E3A8A', light: '#EFF6FF' },
    formulaires: [
      {
        id: 9,
        cerfa: "Questionnaire d'autonomie PCH",
        titre: "Évaluation de l'autonomie",
        qui: 'Rempli par le parent',
        description: "Questionnaire décrivant le niveau d'autonomie de votre enfant dans les actes du quotidien (repas, habillage, déplacements…). Ce document est propre à chaque MDPH — demandez-le directement à votre MDPH départementale ou téléchargez-le depuis son site. Cette page vous explique la PCH enfant et comment en faire la demande.",
        url: 'https://www.monparcourshandicap.gouv.fr/aides/la-pch-pour-les-enfants-et-adolescents-de-moins-de-20-ans',
      },
      {
        id: 10,
        cerfa: 'Transport scolaire adapté',
        titre: 'Demande de transport scolaire adapté',
        qui: 'Rempli par le parent',
        description: "Permet de demander la prise en charge du transport entre le domicile et l'établissement scolaire ou spécialisé de votre enfant. Ce formulaire est propre à chaque département — contactez votre MDPH ou votre Conseil Départemental pour obtenir le formulaire correspondant à votre territoire. La demande est à renouveler chaque année scolaire.",
        url: 'https://www.monparcourshandicap.gouv.fr/scolarite/quelles-sont-les-aides-lautonomie',
      },
      {
        id: 11,
        cerfa: 'Fiche demande logement social adapté',
        titre: 'Demande de logement adapté',
        qui: 'Rempli par le parent',
        description: "Si le handicap de votre enfant nécessite un logement adapté, ce formulaire permet d'en faire la demande auprès des organismes compétents. À combiner avec les devis d'aménagement pour une demande PCH logement.",
        url: 'https://www.monparcourshandicap.gouv.fr/aides/maprimeadapt',
      },
    ],
  },
];

const PIECES_OBLIGATOIRES = [
  { id: 'cerfa_demande', label: 'Cerfa 15692*01 rempli et signé' },
  { id: 'certificat_med', label: 'Cerfa 15695*01 (certificat médical de moins de 12 mois, signé et tamponné)' },
  { id: 'identite', label: "Copie pièce d'identité en cours de validité" },
  { id: 'domicile', label: 'Justificatif de domicile' },
];

const PIECES_COMPLEMENTAIRES = [
  { id: 'bilans_para', label: 'Bilans paramédicaux (orthophonie, psychomotricité, ergothérapie, neuropsychologie…)' },
  { id: 'bilans_sco', label: "Bilans scolaires et comptes rendus d'équipe éducative" },
  { id: 'geva_sco', label: 'GEVA-Sco (première demande ou réexamen)' },
  { id: 'cr_creche', label: 'Compte rendu de crèche (si dernière année avant scolarisation)' },
  { id: 'jugement', label: 'Copie jugement si protection juridique' },
  { id: 'ressources', label: 'Justificatifs de ressources' },
  { id: 'devis', label: 'Devis comparatifs (pour aménagement logement/véhicule via PCH)' },
];

export default function ParentMDPH() {
  const navigate = useNavigate();
  const [openFormulaire, setOpenFormulaire] = useState(null);
  const [openSection, setOpenSection] = useState('base');
  const [checkedPieces, setCheckedPieces] = useState({});
  const [activeTab, setActiveTab] = useState('formulaires');

  const toggleCheck = (id) => setCheckedPieces(prev => ({ ...prev, [id]: !prev[id] }));

  const totalOblig = PIECES_OBLIGATOIRES.length;
  const checkedOblig = PIECES_OBLIGATOIRES.filter(p => checkedPieces[p.id]).length;

  return (
    <div className="px-5 pt-5 pb-6">
      {/* Header */}
      <button onClick={() => navigate('/parent/dashboard')}
        className="flex items-center gap-2 text-sm text-slate-400 font-heading font-semibold mb-5">
        <ArrowLeft size={16} /> Retour au tableau de bord
      </button>

      <div className="mb-5">
        <h1 className="page-title">Espace MDPH</h1>
        <p className="text-sm text-slate-400 font-body mt-0.5">Formulaires et préparation du dossier</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 bg-stone-100 p-1 rounded-2xl">
        <button onClick={() => setActiveTab('formulaires')}
          className={`flex-1 py-2 rounded-xl text-sm font-heading font-semibold transition-all ${activeTab === 'formulaires' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400'}`}>
          Formulaires
        </button>
        <button onClick={() => setActiveTab('dossier')}
          className={`flex-1 py-2 rounded-xl text-sm font-heading font-semibold transition-all ${activeTab === 'dossier' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400'}`}>
          Préparer mon dossier
        </button>
      </div>

      {activeTab === 'formulaires' && (
        <div className="space-y-4">
          {SECTIONS.map(section => {
            const SectionIcon = section.icon;
            const isOpen = openSection === section.id;
            return (
              <div key={section.id} className="rounded-3xl overflow-hidden border border-stone-100 bg-white shadow-card">
                {/* En-tête de section */}
                <button
                  onClick={() => setOpenSection(isOpen ? null : section.id)}
                  className="w-full flex items-center gap-4 p-4 text-left"
                  style={{ backgroundColor: section.couleur.light }}>
                  <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: section.couleur.bg }}>
                    <SectionIcon size={20} style={{ color: section.couleur.text }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-bold text-sm text-slate-800">{section.titre}</p>
                    <p className="text-xs font-body mt-0.5" style={{ color: section.couleur.text }}>{section.description}</p>
                  </div>
                  {isOpen
                    ? <ChevronUp size={16} className="text-slate-400 flex-shrink-0" />
                    : <ChevronDown size={16} className="text-slate-400 flex-shrink-0" />
                  }
                </button>

                {/* Formulaires de la section */}
                {isOpen && (
                  <div className="divide-y divide-stone-50">
                    {section.formulaires.map(f => {
                      const isFormOpen = openFormulaire === f.id;
                      return (
                        <div key={f.id} className="px-4 py-3">
                          <button
                            onClick={() => setOpenFormulaire(isFormOpen ? null : f.id)}
                            className="w-full flex items-center gap-3 text-left">
                            <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: section.couleur.bg }}>
                              <FileText size={14} style={{ color: section.couleur.text }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[10px] font-heading font-bold uppercase tracking-wider"
                                style={{ color: section.couleur.text }}>{f.cerfa}</p>
                              <p className="font-heading font-semibold text-sm text-slate-800 leading-tight">{f.titre}</p>
                              <p className="text-xs text-slate-400 font-body">{f.qui}</p>
                            </div>
                            {isFormOpen
                              ? <ChevronUp size={14} className="text-slate-300 flex-shrink-0" />
                              : <ChevronDown size={14} className="text-slate-300 flex-shrink-0" />
                            }
                          </button>

                          {isFormOpen && (
                            <div className="mt-3 ml-11">
                              <p className="text-sm text-slate-600 font-body leading-relaxed mb-3">{f.description}</p>
                              <a href={f.url} target="_blank" rel="noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl font-heading font-semibold text-sm"
                                style={{ backgroundColor: section.couleur.bg, color: section.couleur.text }}>
                                <Download size={14} />
                                Accéder au formulaire officiel
                              </a>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {activeTab === 'dossier' && (
        <div className="space-y-5">
          {/* Progression */}
          <div className="passerelle-card">
            <p className="font-heading font-bold text-sm text-slate-800 mb-1">Pièces obligatoires</p>
            <p className="text-xs text-slate-400 font-body mb-3">{checkedOblig} sur {totalOblig} réunies</p>
            <div className="w-full h-2 bg-stone-100 rounded-full overflow-hidden">
              <div className="h-2 rounded-full transition-all duration-500"
                style={{ width: `${(checkedOblig / totalOblig) * 100}%`, backgroundColor: '#4A9B8F' }} />
            </div>
            {checkedOblig === totalOblig && (
              <p className="text-xs font-heading font-semibold mt-2" style={{ color: '#4A9B8F' }}>
                ✓ Toutes les pièces obligatoires sont réunies !
              </p>
            )}
          </div>

          {/* Pièces obligatoires */}
          <div>
            <p className="text-xs font-heading font-bold uppercase tracking-widest text-slate-400 mb-3">
              Pièces obligatoires pour toute demande
            </p>
            <div className="space-y-2">
              {PIECES_OBLIGATOIRES.map(piece => (
                <button key={piece.id} onClick={() => toggleCheck(piece.id)}
                  className="w-full passerelle-card flex items-start gap-3 text-left">
                  {checkedPieces[piece.id]
                    ? <CheckCircle size={20} className="flex-shrink-0 mt-0.5" style={{ color: '#4A9B8F' }} />
                    : <Circle size={20} className="flex-shrink-0 mt-0.5 text-stone-300" />
                  }
                  <p className={`text-sm font-body leading-snug ${checkedPieces[piece.id] ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                    {piece.label}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Pièces complémentaires */}
          <div>
            <p className="text-xs font-heading font-bold uppercase tracking-widest text-slate-400 mb-3">
              Pièces complémentaires selon situation
            </p>
            <div className="space-y-2">
              {PIECES_COMPLEMENTAIRES.map(piece => (
                <button key={piece.id} onClick={() => toggleCheck(piece.id)}
                  className="w-full passerelle-card flex items-start gap-3 text-left">
                  {checkedPieces[piece.id]
                    ? <CheckCircle size={20} className="flex-shrink-0 mt-0.5" style={{ color: '#4A9B8F' }} />
                    : <Circle size={20} className="flex-shrink-0 mt-0.5 text-stone-300" />
                  }
                  <p className={`text-sm font-body leading-snug ${checkedPieces[piece.id] ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                    {piece.label}
                  </p>
                </button>
              ))}
            </div>
          </div>

          <div className="passerelle-card border border-amber-100" style={{ backgroundColor: '#FFFBEB' }}>
            <p className="text-xs font-heading font-bold text-amber-700 mb-1">💡 Bon à savoir</p>
            <p className="text-xs text-amber-600 font-body leading-relaxed">
              Les bilans de vos professionnels (orthophonie, psychomotricité…) sont précieux pour votre dossier. Retrouvez-les dans votre bibliothèque de documents Passerelle !
            </p>
          </div>

          <div className="passerelle-card border border-blue-100" style={{ backgroundColor: '#EFF6FF' }}>
            <p className="text-xs font-heading font-bold text-blue-700 mb-1">🎯 Pensez à l'AEEH !</p>
            <p className="text-xs text-blue-600 font-body leading-relaxed mb-2">
              L'<strong>Allocation d'Éducation de l'Enfant Handicapé (AEEH)</strong> est une aide financière versée par la CAF pour compenser les dépenses liées au handicap de votre enfant (soins, matériel, garde spécialisée…). Elle se demande directement dans le <strong>Cerfa 15692*01</strong> — pensez à cocher la bonne case ! C'est souvent la première aide à demander.
            </p>
            <a href="https://www.monparcourshandicap.gouv.fr/aides/lallocation-deducation-de-lenfant-handicape-aeeh"
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-heading font-bold text-blue-700 underline">
              En savoir plus sur l'AEEH →
            </a>
          </div>

          <div className="passerelle-card border border-blue-100" style={{ backgroundColor: '#EFF6FF' }}>
            <p className="text-xs font-heading font-bold text-blue-700 mb-1">🎯 Pensez à l'AEEH !</p>
            <p className="text-xs text-blue-600 font-body leading-relaxed mb-2">
              L'<strong>Allocation d'Éducation de l'Enfant Handicapé (AEEH)</strong> est une aide financière versée par la CAF pour compenser les dépenses liées au handicap de votre enfant (soins, matériel, garde spécialisée…). Elle se demande directement dans le <strong>Cerfa 15692*01</strong> — pensez à cocher la bonne case ! C'est souvent la première aide à demander.
            </p>
            <a href="https://www.monparcourshandicap.gouv.fr/aides/lallocation-deducation-de-lenfant-handicape-aeeh"
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-heading font-bold text-blue-700 underline">
              En savoir plus sur l'AEEH →
            </a>
          </div>

          <div className="passerelle-card border border-blue-100" style={{ backgroundColor: '#EFF6FF' }}>
            <p className="text-xs font-heading font-bold text-blue-700 mb-1">🎯 Pensez à l'AEEH !</p>
            <p className="text-xs text-blue-600 font-body leading-relaxed mb-2">
              L'<strong>Allocation d'Éducation de l'Enfant Handicapé (AEEH)</strong> est une aide financière versée par la CAF pour compenser les dépenses liées au handicap de votre enfant (soins, matériel, garde spécialisée…). Elle se demande directement dans le <strong>Cerfa 15692*01</strong> — pensez à cocher la bonne case ! C'est souvent la première aide à demander.
            </p>
            <a href="https://www.monparcourshandicap.gouv.fr/aides/lallocation-deducation-de-lenfant-handicape-aeeh"
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-heading font-bold text-blue-700 underline">
              En savoir plus sur l'AEEH →
            </a>
          </div>

          <div className="passerelle-card border border-blue-100" style={{ backgroundColor: '#EFF6FF' }}>
            <p className="text-xs font-heading font-bold text-blue-700 mb-1">🎯 Pensez à l'AEEH !</p>
            <p className="text-xs text-blue-600 font-body leading-relaxed mb-2">
              L'<strong>Allocation d'Éducation de l'Enfant Handicapé (AEEH)</strong> est une aide financière versée par la CAF pour compenser les dépenses liées au handicap de votre enfant (soins, matériel, garde spécialisée…). Elle se demande directement dans le <strong>Cerfa 15692*01</strong> — pensez à cocher la bonne case ! C'est souvent la première aide à demander.
            </p>
            <a href="https://www.monparcourshandicap.gouv.fr/aides/lallocation-deducation-de-lenfant-handicape-aeeh"
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-heading font-bold text-blue-700 underline">
              En savoir plus sur l'AEEH →
            </a>
          </div>

          <div className="passerelle-card border border-blue-100" style={{ backgroundColor: '#EFF6FF' }}>
            <p className="text-xs font-heading font-bold text-blue-700 mb-1">🎯 Pensez à l'AEEH !</p>
            <p className="text-xs text-blue-600 font-body leading-relaxed mb-2">
              L'<strong>Allocation d'Éducation de l'Enfant Handicapé (AEEH)</strong> est une aide financière versée par la CAF pour compenser les dépenses liées au handicap de votre enfant (soins, matériel, garde spécialisée…). Elle se demande directement dans le <strong>Cerfa 15692*01</strong> — pensez à cocher la bonne case ! C'est souvent la première aide à demander.
            </p>
            <a href="https://www.monparcourshandicap.gouv.fr/aides/lallocation-deducation-de-lenfant-handicape-aeeh"
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-heading font-bold text-blue-700 underline">
              En savoir plus sur l'AEEH →
            </a>
          </div>
        </div>
      )}
    </div>
  );
}